import { RenderObject } from '../object/RenderObject';
import { AnimationFrame } from './AnimationFrame';

/**
 * Time-tagged event on an animation state. `at` is a phase value in `[0, 1)`
 * — fraction of the clip's total duration. Using phase (not seconds) lets a
 * single `events: [{at: 0.25, name: 'foot_down'}]` work across walk clips of
 * varying length without per-clip tuning.
 *
 * Two storage paths (checked in this order by `dispatchEvents`):
 *
 *  1. **Authored per-clip** — `AnimationState.clipEvents[clipId]` populated
 *     from the model's `_ANIMATION_EVENTS` skin extras. The asset-manager's
 *     animation viewer edits these via the aspect-contributor pattern, so
 *     they survive GLB re-uploads.
 *  2. **Hardcoded per-state** — `allowedStates[currentState].events` (see
 *     `ENTITY_STATE_BEHAVIORS` in `EntityAnimationState`). Used as a
 *     fallback for models that haven't been migrated to authored events.
 *
 * Optional `clipId` lets a single event play its own audio clip directly via
 * `audio.playOneShot(clipId)` — the playful runtime subscribes generically
 * via `onAnyEvent` instead of wiring each event name to a separate handler.
 */
export type AnimationEvent = {
  at: number;
  name: string;
  /** When set, listeners receive this so they can `audio.playOneShot(clipId)` without an external name→clip mapping. */
  clipId?: string;
};

/**
 * Animation event names that an engine consumer subscribes to via
 * `AnimationState.onEvent(name, ...)`. Surfaced to the asset-manager
 * animation viewer as autocomplete suggestions — authors can still type
 * arbitrary names (any event with `clipId` set self-plays through the
 * `onAnyEvent` route in playful, no named listener needed), but well-known
 * names that route through a wired handler should appear here so they're
 * discoverable instead of having to be remembered.
 *
 * Append to this array when wiring a new named handler in playful or
 * asset-manager. Keep alphabetised.
 */
export const KNOWN_ANIMATION_EVENT_NAMES = ['foot_down'] as const;

export class AnimationState {
  public renderObject: RenderObject;
  public currentState: string = 'Idle';
  private animationStart: number;
  private pausedAt?: number;

  private previousState?: string;
  private blendStart?: number;
  private blendDuration: number = 300; // ms

  public allowedStates: StateDefinition = {};
  /**
   * Authored per-clip events keyed by glTF animation name. Populated by
   * `AnimationStateFactory` from `_ANIMATION_EVENTS` skin extras. When an
   * entry exists for the active clip, it overrides `allowedStates[state].events`.
   */
  public clipEvents?: Record<string, readonly AnimationEvent[]>;
  private warnedStates = new Set<string>();
  private warnedClips = new Set<string>();

  /** Per-named-event listener bags. `onEvent` returns an unsubscribe. */
  private eventListeners = new Map<string, Array<(event: AnimationEvent) => void>>();
  /** Listeners that fire for every event regardless of name. Used by the generic clipId-play path. */
  private anyEventListeners: Array<(event: AnimationEvent) => void> = [];
  /** Tracks the previous in-clip elapsed seconds so we can detect event crossings + loop wraps. */
  private eventTracker: { clipId: string; lastElapsed: number } | null = null;

  constructor(object: RenderObject) {
    this.renderObject = object;
    this.animationStart = Date.now();
  }

  protected resolveClipId(stateName: string): string {
    return stateName;
  }

  public pause() {
    if (this.pausedAt === undefined) {
      this.pausedAt = Date.now();
    }
  }

  public resume() {
    if (this.pausedAt !== undefined) {
      // Shift animationStart forward by the paused duration so the animation
      // continues from the frame it was at when paused.
      const pausedDuration = Date.now() - this.pausedAt;
      this.animationStart += pausedDuration;
      if (this.blendStart !== undefined) {
        this.blendStart += pausedDuration;
      }
      this.pausedAt = undefined;
    }
  }

  public get paused(): boolean {
    return this.pausedAt !== undefined;
  }

  /**
   * Jump the active clip's playback head to `elapsedSec` past the start of
   * the clip cycle. Respects pause state — if currently paused, the next
   * `resume()` continues from the seeked position; if playing, the
   * animation continues forward from there.
   *
   * Drives the editor's frame scrubber. Resets the event tracker so events
   * past the seek point can re-fire on the new cycle without needing a loop
   * wrap.
   */
  public seekTo(elapsedSec: number): void {
    // `elapsedSec` is in clip-time. The renderer's `computeFrameIndex`
    // schedules the frame at clip-time `t` to display at wall time
    // `animationStart + t * speed * 1000` (so `speed > 1` means slower
    // playback in this engine). To render clip-frame `c` right now, pin
    // `animationStart + c * speed * 1000 == now`. Inverse of
    // `clipTimeSec` below.
    const speed = this.allowedStates[this.currentState]?.speed ?? 1;
    const safeSpeed = speed > 0 ? speed : 1;
    const targetMs = Math.max(0, elapsedSec) * safeSpeed * 1000;
    if (this.pausedAt !== undefined) {
      this.animationStart = this.pausedAt - targetMs;
    } else {
      this.animationStart = Date.now() - targetMs;
    }
    this.eventTracker = null;
  }

  /** Active clip's total duration in seconds, or 0 if no animation/state is set. */
  public getCurrentClipDurationSec(): number {
    if (!this.renderObject.animation) return 0;
    const clipId = this.resolveClipId(this.currentState);
    const frames = this.renderObject.animation.joints[0]?.frames[clipId];
    if (!frames || frames.length === 0) return 0;
    return frames[frames.length - 1].timeStep;
  }

  /**
   * Current elapsed seconds into the active clip — i.e. the clip-time the
   * renderer is showing right now, computed as the inverse of
   * `computeFrameIndex`'s scheduling formula. Wrapped into `[0, duration)`
   * so a scrubber bound to phase stays in range.
   */
  public getCurrentElapsedSec(): number {
    const now = this.pausedAt ?? Date.now();
    const speed = this.allowedStates[this.currentState]?.speed ?? 1;
    const elapsed = AnimationState.clipTimeSec(now - this.animationStart, speed);
    const duration = this.getCurrentClipDurationSec();
    if (duration <= 0) return 0;
    const wrapped = elapsed % duration;
    return wrapped < 0 ? wrapped + duration : wrapped;
  }

  /**
   * Convert a wall-clock delta to clip-time using the engine's
   * `speed > 1 = slower` convention, matching `computeFrameIndex`'s
   * scheduling formula. Centralised so the three sites that need the
   * conversion (renderer, scrubber readback, event dispatch) can't drift
   * out of alignment again — the previous bug was that `dispatchEvents`'
   * caller multiplied by speed while the renderer divided.
   */
  private static clipTimeSec(deltaMs: number, speed: number): number {
    const safeSpeed = speed > 0 ? speed : 1;
    return deltaMs / 1000 / safeSpeed;
  }

  public switchState(newState: string) {
    let state = this.allowedStates[newState];
    if (state && newState !== this.currentState) {
      // Only blend if the previous state is valid (has an entry in allowedStates)
      if (this.allowedStates[this.currentState]) {
        this.previousState = this.currentState;
        this.blendStart = Date.now();
      }
      this.currentState = newState;
      this.animationStart = Date.now();
      // State change resets the event tracker so we don't fire stale events
      // for the new clip based on the old clip's elapsed time.
      this.eventTracker = null;
    }
  }

  /**
   * Subscribe to a named animation event (e.g. `'foot_down'`). Returns an
   * unsubscribe function. Multiple listeners per name are supported and fire
   * in registration order. Throwing handlers are caught and logged so one
   * bad listener doesn't break the rest. The callback receives the full
   * `AnimationEvent` so it can read optional fields like `clipId`.
   */
  public onEvent(name: string, callback: (event: AnimationEvent) => void): () => void {
    let bag = this.eventListeners.get(name);
    if (!bag) {
      bag = [];
      this.eventListeners.set(name, bag);
    }
    bag.push(callback);
    return () => {
      const list = this.eventListeners.get(name);
      if (!list) return;
      const idx = list.indexOf(callback);
      if (idx >= 0) list.splice(idx, 1);
      if (list.length === 0) this.eventListeners.delete(name);
    };
  }

  /**
   * Subscribe to every animation event regardless of name. Returns an
   * unsubscribe. Intended for the generic "play `event.clipId` if set"
   * routing — one subscription instead of one-per-event-name. Named-event
   * listeners still fire normally.
   */
  public onAnyEvent(callback: (event: AnimationEvent) => void): () => void {
    this.anyEventListeners.push(callback);
    return () => {
      const idx = this.anyEventListeners.indexOf(callback);
      if (idx >= 0) this.anyEventListeners.splice(idx, 1);
    };
  }

  /**
   * Fire any event in the current state's `events` whose phase was crossed
   * between the previous tick and `nowElapsed`. Handles loop wraps (when
   * the existing 'always' repeat resets `animationStart` and `nowElapsed`
   * jumps back near zero).
   */
  private dispatchEvents(clipId: string, nowElapsedSec: number, durationSec: number): void {
    // Authored per-clip events win over hardcoded state events — the asset
    // manager's authoring path is the canonical source once a model has been
    // migrated; the state-level fallback only fires for legacy/unmigrated
    // models. The two are mutually exclusive per-tick so listeners don't
    // see duplicates.
    const events = this.clipEvents?.[clipId] ?? this.allowedStates[this.currentState]?.events;
    if (!events || events.length === 0 || durationSec <= 0) {
      this.eventTracker = null;
      return;
    }
    if (!this.eventTracker || this.eventTracker.clipId !== clipId) {
      this.eventTracker = { clipId, lastElapsed: 0 };
    }
    const prev = this.eventTracker.lastElapsed;
    const curr = nowElapsedSec;
    for (const e of events) {
      const t = e.at * durationSec;
      const fired =
        curr >= prev
          ? t > prev && t <= curr
          : // Loop wrap: elapsed reset by 'always' repeat. Fire anything in
            // either the tail of the previous cycle or the head of this one.
            t > prev || t <= curr;
      if (fired) this.fireEvent(e);
    }
    this.eventTracker.lastElapsed = curr;
  }

  private fireEvent(event: AnimationEvent): void {
    const named = this.eventListeners.get(event.name);
    if (named) {
      for (const cb of named) {
        try {
          cb(event);
        } catch (err) {
          console.error(`[AnimationState] event "${event.name}" handler threw:`, err);
        }
      }
    }
    for (const cb of this.anyEventListeners) {
      try {
        cb(event);
      } catch (err) {
        console.error(`[AnimationState] onAnyEvent handler threw on "${event.name}":`, err);
      }
    }
  }

  public getFrames(): FrameSet {
    const state = this.allowedStates[this.currentState];
    if (!state) {
      if (!this.warnedStates.has(this.currentState)) {
        this.warnedStates.add(this.currentState);
        const available = Object.keys(this.allowedStates);
        const isIdentityMapping = this.constructor === AnimationState;
        const hint = isIdentityMapping
          ? ` (no _ANIMATION_STATE configured — using raw clip names as states)`
          : '';
        console.warn(
          `[AnimationState] State "${this.currentState}" not found on "${this.renderObject.mesh.reference}"${hint}. ` +
            `Available: [${available.join(', ')}]`,
        );
      }
      return {
        frames: {},
        alpha: 0,
        state: this.currentState,
        stateName: this.currentState,
        frameIndex: 0,
      };
    }
    const now = this.pausedAt ?? Date.now();
    const joints = this.renderObject.animation!.joints;
    const clipId = this.resolveClipId(this.currentState);

    // Compute frame index and interpolation alpha for current clip
    const { frameIndex, alpha, last } = this.computeFrameIndex(
      clipId,
      state.speed,
      this.animationStart,
      now,
    );

    // Dispatch animation events before updating tracker. `clipTimeSec`
    // matches the renderer's `start + timeStep * speed * 1000` scheduling
    // so events fire at the same clip phase the player is seeing — not the
    // `wall * speed` value that used to live here, which desynced events
    // from the rendered pose at any speed != 1.
    const referenceFrames = joints[0].frames[clipId];
    if (referenceFrames && referenceFrames.length > 0) {
      const durationSec = referenceFrames[referenceFrames.length - 1].timeStep;
      const elapsedSec = AnimationState.clipTimeSec(now - this.animationStart, state.speed);
      this.dispatchEvents(clipId, elapsedSec, durationSec);
    }

    // Build frame map using computed index (all joints share same timebase)
    const frames: { [key: string]: AnimationFrame } = {};
    // For attachment interpolation: prevFrames is the frame before frameIndex,
    // alpha is how far between prevFrames and frames we are.
    let prevFrames: { [key: string]: AnimationFrame } | undefined;
    for (const joint of joints) {
      const jointFrames = joint.frames[clipId];
      frames[joint.name] = jointFrames[Math.min(frameIndex, jointFrames.length - 1)];
      if (alpha > 0 && frameIndex > 0) {
        if (!prevFrames) prevFrames = {};
        prevFrames[joint.name] = jointFrames[frameIndex - 1];
      }
    }

    const result: FrameSet = {
      frames,
      prevFrames,
      alpha,
      frameIndex,
      state: clipId,
      stateName: this.currentState,
    };

    // Compute blend data if transitioning
    if (this.previousState && this.blendStart) {
      const elapsed = now - this.blendStart;
      const weight = Math.min(elapsed / this.blendDuration, 1.0);

      if (weight < 1.0) {
        const prevStateConfig = this.allowedStates[this.previousState];
        const prevClipId = this.resolveClipId(this.previousState);
        const { frameIndex: prevFrameIndex } = this.computeFrameIndex(
          prevClipId,
          prevStateConfig.speed,
          this.blendStart,
          now,
        );

        const blendFrames: { [key: string]: AnimationFrame } = {};
        for (const joint of joints) {
          const jointFrames = joint.frames[prevClipId];
          blendFrames[joint.name] = jointFrames[Math.min(prevFrameIndex, jointFrames.length - 1)];
        }

        result.blend = {
          frames: blendFrames,
          state: prevClipId,
          stateName: this.previousState,
          frameIndex: prevFrameIndex,
          weight,
        };
      } else {
        this.previousState = undefined;
        this.blendStart = undefined;
      }
    }

    // Handle clip completion
    if (last) {
      switch (state.repeat) {
        case 'always':
          this.animationStart = Date.now();
          break;
        case 'once':
          if (state.nextState) {
            this.switchState(state.nextState);
          }
          break;
      }
    }

    return result;
  }

  private computeFrameIndex(
    clipId: string,
    speed: number,
    startTime: number,
    now: number,
  ): { frameIndex: number; alpha: number; last: boolean } {
    const joints = this.renderObject.animation!.joints;
    const referenceFrames = joints[0].frames[clipId];
    if (!referenceFrames) {
      if (!this.warnedClips.has(clipId)) {
        this.warnedClips.add(clipId);
        console.warn(
          `[AnimationState] Animation clip "${clipId}" not found on "${this.renderObject.mesh.reference}". ` +
            `Available clips in GLB: [${Object.keys(joints[0].frames).join(', ')}]`,
        );
      }
      return { frameIndex: 0, alpha: 0, last: true };
    }

    let frameIndex = referenceFrames.length - 1;
    let alpha = 0;
    let last = true;

    for (let f = 0; f < referenceFrames.length; f++) {
      if (startTime + referenceFrames[f].timeStep * speed * 1000 > now) {
        frameIndex = f;
        last = false;
        // Compute interpolation alpha between previous frame and this one
        if (f > 0) {
          const prevTime = startTime + referenceFrames[f - 1].timeStep * speed * 1000;
          const currTime = startTime + referenceFrames[f].timeStep * speed * 1000;
          alpha = (now - prevTime) / (currTime - prevTime);
        }
        break;
      }
    }

    return { frameIndex, alpha, last };
  }
}

export type StateDefinition = {
  [key: string]: {
    repeat: string;
    speed: number;
    nextState?: string;
    /** Optional list of named events fired during clip playback. See {@link AnimationEvent}. */
    events?: readonly AnimationEvent[];
  };
};

export type FrameSet = {
  frames: { [key: string]: AnimationFrame };
  prevFrames?: { [key: string]: AnimationFrame };
  alpha: number;
  state: string;
  stateName: string;
  frameIndex: number;
  blend?: {
    frames: { [key: string]: AnimationFrame };
    state: string;
    stateName: string;
    frameIndex: number;
    weight: number;
  };
};
